import { resultRevisionKey } from '../utils/resultRevisions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTabActivity } from '../contexts/TabActivityContext';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Film, LayoutGrid, RefreshCw, Search } from 'lucide-react';
import {
  apiService,
  BatchVideoSegmentsTarget,
  getApiErrorMessage,
  PromptRun,
  PromptRunWorkflowSegmentResult,
  PromptRunWorkflowVideoResult,
  SearchResult,
  Video,
  VideoPromptRunResult,
} from '../services/api';
import { segmentCache } from '../services/segmentCache';
import { useSearchWorkspace } from '../contexts/SearchWorkspaceContext';
import { toGallerySearchResult } from '../features/search-workspace/useBrowseGallery';
import { promptRunHasSelectionFilter } from '../features/workflows/config';
import { mapWithConcurrency } from '../utils/mapWithConcurrency';
import { isPromptRunTerminalStatus } from '../utils/promptRunFailures';
import { sanitizeRunVideoIds } from '../utils/promptRunProgress';
import { getSearchResultKey } from '../utils/searchResultIdentity';
import { matchesMetadataConditions } from '../utils/filterUtils';
import { getStudioConditionFields, studioSearchText } from '../features/playground/studioResultFiltering';
import { createPlaygroundStudioWorkspaceState, PlaygroundStudioWorkspaceState } from '../features/playground/studioWorkspaceState';
import StudioResultToolbar from './StudioResultToolbar';
import SearchResultDetailModal from './SearchResultDetailModal';
import SearchResultsGallery from './search-workspace/SearchResultsGallery';
import './WorkflowRunResults.css';

const PREVIEW_LIMIT = 20;
type ResultMode = 'matches' | 'all';
type ResultLevel = 'segment' | 'video';
type LoadVideoSummaries = (videoIds: string[], signal: AbortSignal) => Promise<Video[]>;
interface ResultPreview {
  results: SearchResult[];
  limited: boolean;
  unavailable?: boolean;
}
interface CachedPreview {
  key: string;
  page: ResultPreview;
}

const hydratePreviewMetadata = async (page: ResultPreview, runId: string, signal: AbortSignal): Promise<ResultPreview> => {
  let unavailable = page.unavailable;
  const results = await mapWithConcurrency(page.results, 4, async (result) => {
    if (signal.aborted || !result.segment_id || result.metadata !== undefined) return result;
    const cached = segmentCache.peekDetailSegment(result.video_id, runId, result.segment_id);
    try {
      // Gallery pages contain media references only. Hydrate exactly this
      // bounded preview once; filtering never fetches or scans the full run.
      const detail = cached || await apiService.getVideoSegment(result.video_id, result.segment_id, runId, signal);
      if (!detail || signal.aborted) { unavailable = true; return result; }
      if (!cached) segmentCache.primeSegmentProjection(result.video_id, runId, [detail], { completeness: 'partial', projection: 'detail' });
      return { ...result, metadata: detail.metadata, metadata_text: detail.metadata_text, text_content: detail.metadata_text || '' };
    } catch (error: any) {
      if (error?.response?.status !== 404) throw error;
      unavailable = true;
      return result;
    }
  });
  return { ...page, results, unavailable };
};

const toVideoSearchResult = (
  video: Video,
  runId: string,
  indexId: string,
  output: Pick<VideoPromptRunResult, 'metadata' | 'metadata_text'> & Partial<VideoPromptRunResult>,
): SearchResult => ({
  ...output,
  raw_llm_response: output.raw_llm_response || undefined,
  result_type: 'video',
  result_id: `video:${video.video_id}:${runId}`,
  video_id: video.video_id,
  video_name: video.title,
  video_uri: video.video_uri,
  segment_id: null,
  start_time: null,
  end_time: null,
  thumbnail_uri: undefined,
  gif_uri: undefined,
  thumbnail_available: Boolean(output.thumbnail_gcs_uri),
  gif_available: Boolean(output.gif_gcs_uri),
  video_thumbnail_gcs_uri: video.thumbnail_gcs_uri,
  video_thumbnail_available: Boolean(video.thumbnail_gcs_uri),
  video_gif_gcs_uri: video.gif_gcs_uri,
  video_gif_available: Boolean(video.gif_gcs_uri),
  media_type: video.media_type as SearchResult['media_type'],
  text_content: output.metadata_text || '',
  similarity_score: null,
  run_id: runId,
  prompt_run_id: runId,
  source_index_id: indexId,
  marker: video.marker,
});

const loadMatchedPreview = async (
  runId: string,
  indexId: string,
  level: ResultLevel,
  signal: AbortSignal,
  loadVideoSummaries: LoadVideoSummaries,
): Promise<ResultPreview> => {
  const page = await apiService.getPromptRunWorkflowResults(runId, level, PREVIEW_LIMIT, undefined, { signal });
  if (signal.aborted) return { results: [], limited: false };
  // The video endpoint also includes parent context for segment-only matches.
  // Only explicit whole-video matches belong in the Videos view.
  const rows = level === 'video'
    ? page.data.filter((row): row is PromptRunWorkflowVideoResult => (
        !('segment_id' in row) && row.video_matched
      ))
    : page.data.filter((row): row is PromptRunWorkflowSegmentResult => 'segment_id' in row);
  const videoIds = Array.from(new Set(rows.map((row) => row.video_id)));
  const videos = videoIds.length ? await loadVideoSummaries(videoIds, signal) : [];
  const videoById = new Map(videos.map((video) => [video.video_id, video]));
  const results = rows.flatMap((row): SearchResult[] => {
    const video = videoById.get(row.video_id);
    if (!video) return [];
    if ('segment_id' in row) {
      return row.segment ? [toGallerySearchResult(row.segment, video, runId, indexId)] : [];
    }
    return row.video ? [toVideoSearchResult(video, runId, indexId, {
      metadata: row.video.extracted_metadata || {},
      metadata_text: row.video.metadata_text || '',
    })] : [];
  });
  return {
    results,
    limited: page.pagination.has_more || results.length < rows.length,
    unavailable: results.length < rows.length,
  };
};

const loadExtractedPreview = async (
  runId: string,
  indexId: string,
  videoIds: string[],
  level: ResultLevel,
  signal: AbortSignal,
  loadVideoSummaries: LoadVideoSummaries,
): Promise<ResultPreview> => {
  // Like Index, browse the run's immutable media membership, not today's index.
  // This small preview never fans out across the full execution.
  const pageIds = videoIds.slice(0, PREVIEW_LIMIT);
  if (!pageIds.length) return { results: [], limited: false };
  const summaries = await loadVideoSummaries(pageIds, signal);
  const videoById = new Map(summaries.map((video) => [video.video_id, video]));
  const videos = pageIds.flatMap((id) => videoById.has(id) ? [videoById.get(id)!] : []);
  let limited = videoIds.length > pageIds.length || videos.length < pageIds.length;
  if (level === 'video') {
    let unavailable = videos.length < pageIds.length;
    const results = await mapWithConcurrency(videos.filter((video) => video.media_type !== 'image'), 4, async (video) => {
      if (signal.aborted) return null;
      try {
        const output = await apiService.getPromptRunVideoResult(runId, video.video_id, signal);
        return output.status === 'successful' ? toVideoSearchResult(video, runId, indexId, output) : null;
      } catch (error: any) {
        // Partial runs can have absent output. Show that absence explicitly;
        // never invent a video result or retry extraction to fill the preview.
        if (error?.response?.status === 404) {
          unavailable = true;
          return null;
        }
        throw error;
      }
    });
    return { results: results.filter((result): result is SearchResult => Boolean(result)), limited: limited || unavailable, unavailable };
  }

  const results: SearchResult[] = [];
  let remaining = PREVIEW_LIMIT;
  let pending: BatchVideoSegmentsTarget[] = videos.map((video) => ({ video_id: video.video_id, run_id: runId }));
  while (remaining > 0 && pending.length > 0 && !signal.aborted) {
    const targets = pending.slice(0, remaining);
    const limit = Math.max(1, Math.floor(remaining / targets.length));
    const groups = await segmentCache.loadBatchSegments(
      targets.map((target) => ({ ...target, limit })),
      { maxAttempts: 1 },
    );
    const nextTargets = pending.slice(targets.length);
    let received = 0;
    groups.forEach((group) => {
      const video = videoById.get(group.video_id);
      received += group.segments.length;
      if (video) {
        group.segments.forEach((segment) => {
          // The shared gallery projection contains run-scoped media references.
          // The bounded preview is hydrated below for local field filtering.
          results.push(toGallerySearchResult(segment, video, runId, indexId));
        });
      }
      if (group.has_more && group.next_cursor) {
        nextTargets.push({ video_id: group.video_id, run_id: runId, cursor: group.next_cursor });
      }
    });
    pending = nextTargets;
    remaining -= received;
    if (received === 0) break;
  }
  limited = limited || pending.length > 0;
  return { results: results.slice(0, PREVIEW_LIMIT), limited };
};

const WorkflowRunResults: React.FC<{ run: PromptRun; indexId: string; isActive?: boolean }> = ({ run, indexId, isActive: requestedIsActive = true }) => {
  const { isActive: isTabActive, portalContainer } = useTabActivity();
  const isActive = requestedIsActive && isTabActive;
  const navigate = useNavigate();
  const { getVideoSummaries, primeVideoSummaries } = useSearchWorkspace();
  const loadVideoSummaries = useCallback<LoadVideoSummaries>(async (ids, signal) => {
    const cached = getVideoSummaries(ids);
    const cachedIds = new Set(cached.map((video) => video.video_id));
    const missingIds = ids.filter((id) => !cachedIds.has(id));
    if (!missingIds.length) return cached;
    const loaded = await apiService.getBatchVideoSummaries(missingIds, signal);
    if (!signal.aborted) primeVideoSummaries(loaded);
    return [...cached, ...loaded];
  }, [getVideoSummaries, primeVideoSummaries]);
  const hasFilter = promptRunHasSelectionFilter(run);
  const [requestedMode, setRequestedMode] = useState<{ runId: string; mode: ResultMode } | null>(null);
  const [requestedLevel, setRequestedLevel] = useState<ResultLevel>('segment');
  const [previewState, setPreviewState] = useState<CachedPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [resultTools, setResultTools] = useState<Record<ResultLevel, PlaygroundStudioWorkspaceState>>(() => ({
    segment: createPlaygroundStudioWorkspaceState(null, run.run_id),
    video: createPlaygroundStudioWorkspaceState(null, run.run_id),
  }));
  // A visited execution owns at most four 20-result previews: two modes × two
  // levels. Keep requests alive across view switches so returning joins them.
  const cacheRef = useRef(new Map<string, CachedPreview>());
  const pendingRef = useRef(new Map<string, { controller: AbortController; request: Promise<ResultPreview> }>());
  const membership = JSON.stringify(sanitizeRunVideoIds(run));
  const videoIds = useMemo<string[]>(() => JSON.parse(membership), [membership]);
  const effectiveMode = hasFilter
    ? requestedMode?.runId === run.run_id ? requestedMode.mode : 'all'
    : 'all';
  const setMode = (mode: ResultMode) => setRequestedMode({ runId: run.run_id, mode });
  const selectionReady = run.selection_summary?.status === 'ready';
  const terminal = isPromptRunTerminalStatus(run.status);
  const availableLevels: ResultLevel[] = effectiveMode === 'matches'
    ? [
        ...((run.selection_summary?.selected_segment_count || 0) > 0 ? ['segment' as const] : []),
        ...((run.selection_summary?.selected_video_count || 0) > 0 ? ['video' as const] : []),
      ]
    : [
        'segment',
        ...(run.video_level_enabled || (run.video_level_completed_items || 0) > 0 ? ['video' as const] : []),
      ];
  const level = availableLevels.includes(requestedLevel) ? requestedLevel : availableLevels[0] || 'segment';
  const resultCount = effectiveMode === 'matches'
    ? level === 'segment' ? run.selection_summary?.selected_segment_count : run.selection_summary?.selected_video_count
    : level === 'segment' ? run.completed_segments : run.video_level_completed_items;
  const cacheKey = `${indexId}:${run.run_id}:${effectiveMode}:${level}:${resultRevisionKey(run.result_revisions)}:${resultCount ?? 'unknown'}`;
  const viewKey = `${effectiveMode}:${level}`;
  const cachedPreview = cacheRef.current.get(viewKey);
  const preview = previewState?.key === cacheKey ? previewState.page
    : cachedPreview?.key === cacheKey ? cachedPreview.page : null;

  useEffect(() => {
    setSelectedResult(null);
  }, [cacheKey]);

  useEffect(() => {
    const pendingRequests = pendingRef.current;
    return () => {
      pendingRequests.forEach(({ controller }) => controller.abort());
      pendingRequests.clear();
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;
    setError(null);
    if (effectiveMode === 'matches' && !selectionReady) {
      setPreviewState(null);
      return;
    }
    // Selection counts are the published output manifest. Extraction counters
    // on older runs can default to zero, so read their bounded gallery instead.
    if (effectiveMode === 'matches' && resultCount === 0) {
      setPreviewState({ key: cacheKey, page: { results: [], limited: false } });
      return;
    }
    const cached = cacheRef.current.get(viewKey);
    if (cached?.key === cacheKey) {
      setPreviewState(cached);
      return;
    }
    setPreviewState(null);
    let pending = pendingRef.current.get(cacheKey);
    if (!pending) {
      const controller = new AbortController();
      const request = (effectiveMode === 'matches'
        ? loadMatchedPreview(run.run_id, indexId, level, controller.signal, loadVideoSummaries)
        : loadExtractedPreview(run.run_id, indexId, videoIds, level, controller.signal, loadVideoSummaries)
      ).then((page) => level === 'segment' ? hydratePreviewMetadata(page, run.run_id, controller.signal) : page).then((page) => {
        if (!controller.signal.aborted) cacheRef.current.set(viewKey, { key: cacheKey, page });
        return page;
      }).finally(() => {
        if (pendingRef.current.get(cacheKey)?.controller === controller) pendingRef.current.delete(cacheKey);
      });
      pending = { controller, request };
      pendingRef.current.set(cacheKey, pending);
    }
    let current = true;
    const { controller, request } = pending;
    void request.then((page) => {
      if (current && !controller.signal.aborted) setPreviewState({ key: cacheKey, page });
    }).catch((loadError) => {
      if (current && !controller.signal.aborted) setError(getApiErrorMessage(loadError, 'Could not load this result preview.'));
    });
    return () => { current = false; };
  }, [cacheKey, effectiveMode, indexId, isActive, level, loadVideoSummaries, resultCount, retry, run.run_id, selectionReady, terminal, videoIds, viewKey]);

  const openIndex = () => navigate(`/index?${new URLSearchParams({ index: indexId, run: run.run_id, result: effectiveMode })}`);
  const openSearch = () => navigate(`/search?${new URLSearchParams({
    indexes: indexId,
    runs: run.run_id,
    mode: 'vector',
    panel: 'search',
    view: level === 'segment' ? 'segments' : 'videos',
    q: '',
    fields: '',
    filters: '',
    detail: '',
    submitted: '0',
  })}`);
  const indexRuns = useMemo(() => ({ [indexId]: [run] }), [indexId, run]);
  const allResults = preview?.results || [];
  const toolState = resultTools[level].scopeRunId === run.run_id
    ? resultTools[level] : createPlaygroundStudioWorkspaceState(null, run.run_id);
  const fields = useMemo(() => getStudioConditionFields(preview?.results.map((result) => result.metadata) || []), [preview]);
  const query = toolState.resultSearchTerm.trim().replace(/_/g, ' ').toLocaleLowerCase();
  const group = toolState.resultFilter?.[level];
  const results = allResults.filter((result) => (
    (!query || studioSearchText(result.metadata, `${result.metadata_text || result.text_content || ''} ${result.video_name || ''}`).includes(query))
    && (!group || matchesMetadataConditions(result.metadata || {}, group.conditions.map((condition) => ({ ...condition, value: condition.value })), group.logic))
  ));
  const waitingForSelection = effectiveMode === 'matches' && !selectionReady;

  return (
    <section className="workflow-run-results" aria-label="Execution results">
      <StudioResultToolbar
        key={`${run.run_id}:${level}`}
        state={toolState}
        onChange={(patch) => setResultTools((current) => ({ ...current, [level]: { ...toolState, ...patch } }))}
        segmentFields={level === 'segment' ? fields : []}
        videoFields={level === 'video' ? fields : []}
        runFilter={run.selection_filter || null}
        hasRunFilter={hasFilter}
        resultMode={effectiveMode}
        onResultModeChange={setMode}
        trailingControls={<>
        {availableLevels.length > 1 && (
          <div className="workflow-result-switch" aria-label="Result level">
            {availableLevels.map((item) => (
              <button key={item} type="button" className={level === item ? 'active' : ''} aria-pressed={level === item} onClick={() => setRequestedLevel(item)}>
                {item === 'segment' ? <LayoutGrid size={13} /> : <Film size={13} />}
                {item === 'segment' ? 'Segments' : 'Videos'}
              </button>
            ))}
          </div>
        )}
        <div className="workflow-result-actions">
          <button type="button" onClick={openSearch} title="Search all extracted results in this execution">
            <Search size={14} /> Search these results
          </button>
          <button type="button" onClick={openIndex}>See full results in Index <ArrowUpRight size={14} /></button>
        </div>
        </>}
      />

      {preview?.unavailable && (
        <p className="workflow-result-unavailable" role="status">Some media in this preview has no readable output. Only available results are shown.</p>
      )}

      {waitingForSelection ? (
        <p className="workflow-result-message" role="status">
          {run.selection_summary?.status === 'failed' ? 'The engine filter could not be applied. Turn it off to browse all extracted results.'
              : 'Matches are not published yet. Turn off the engine filter to browse all extracted results.'}
        </p>
      ) : error ? (
        <div className="workflow-result-message workflow-result-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setRetry((value) => value + 1)}><RefreshCw size={13} /> Retry</button>
        </div>
      ) : !preview ? (
        <p className="workflow-result-message" role="status"><RefreshCw size={14} className="workflow-result-spinner" /> Loading results…</p>
      ) : results.length === 0 ? (
        <p className="workflow-result-message">
          {query || toolState.resultFilter ? 'No results in this preview match your search and conditions.' : preview.limited
            ? `No ${level === 'video' ? 'whole-video' : 'segment'} results in this limited preview. Open the full execution in Index to browse the remaining media.`
            : effectiveMode === 'matches' ? 'No results matched this engine’s filter.' : 'No extracted results are available at this level.'}
        </p>
      ) : (
        <div className="workflow-run-results-gallery">
          <SearchResultsGallery
            results={results}
            runs={[run]}
            compact
            view={level === 'segment' ? 'segments' : 'videos'}
            onResultClick={setSelectedResult}
            selectedResultKey={selectedResult ? getSearchResultKey(selectedResult) : null}
          />
        </div>
      )}

      <div className="workflow-run-results-footer">
        <span>{preview && results.length > 0 ? `${results.length} result${results.length === 1 ? '' : 's'} in this preview · Up to ${PREVIEW_LIMIT} shown` : `Preview limited to ${PREVIEW_LIMIT} results`}</span>
      </div>
      {createPortal(<SearchResultDetailModal
        result={selectedResult}
        isOpen={Boolean(selectedResult)}
        isVisible={isActive}
        onClose={() => setSelectedResult(null)}
        indexRuns={indexRuns}
        allSearchResults={results}
      />, portalContainer || document.body)}
    </section>
  );
};

export default WorkflowRunResults;
